import { useState } from "react";
import { Button } from "@a2z/ui";
import { endpoints } from "@/lib/api/endpoints";
import { useTranslate } from "@/lib/i18n";
import { queryKeys, useApiQuery } from "@/shared/queries";
import { useSession, hasPermission, PERMISSIONS } from "@/modules/auth/useSession";
import { OpenDrawerDialog } from "./OpenDrawerDialog";
import type { RestaurantSettings } from "@/lib/api/types";

/**
 * NO DRAWER IS OPEN, SAID ON THE FLOOR RATHER THAN AT THE TILL (Unit C).
 *
 * The prompt used to arrive at the worst possible moment: the cashier had taken
 * the order, printed the cheque and had the customer's cash in hand when the settle
 * sheet told them money could not be taken. By then the only options are to lose
 * the customer's patience or to put the notes in the till and record nothing —
 * and the second is what actually happens, which makes the warning the CAUSE of
 * the untracked payment it exists to prevent.
 *
 * So it moves to the start: this strip rides every till screen — the floor grid, a
 * table, a bill — from the moment the app loads, so a cashier arriving for service
 * sees it before they have touched anything.
 *
 * **It is a strip and not a block.** The persona review was unanimous that blocking
 * pushes service off the system: a till that refuses to ring a bill gets a paper
 * pad, and a meal on a paper pad is invisible to the books, to the tax return, and
 * to every control in this product. A null-drawer invoice is a declarable sale with
 * a weak custody label; an off-system meal is nothing at all.
 *
 * **Two audiences, two sentences.** A holder of the drawer rung gets the
 * instruction AND the button. Someone who may take money but may not open a
 * drawer — a custom role built without `SHIFT_MANAGE` — is told who to ask,
 * because "open a drawer" is a dead instruction for them and a dead instruction
 * teaches people to ignore the strip.
 *
 * **And a THIRD audience, which gets nothing.** `shifts/current` answers for the
 * MEMBER, not the store, and only `SHIFT_MANAGE` can ever open a drawer — so for
 * a waiter it is null from the first screen of the shift to the last and the
 * strip never clears, whatever the cashier beside them has open. The refusal it
 * warns about cannot reach them either: the WAITER preset holds no
 * `RESTAURANT_INVOICE_SETTLE`, so no tender of theirs is ever refused for want of
 * a drawer. A permanent warning about a refusal that cannot happen to you is the
 * "dead instruction" above, one level up — it is how a floor learns to read past
 * this strip on the night it finally means something. So it is gated on the
 * permission the refusing endpoint actually requires (`settleInvoice` →
 * `RESTAURANT_INVOICE_SETTLE`), exactly as every other screen in this app gates
 * on the endpoint's own permission rather than on a role name.
 *
 * Renders NOTHING when tracking is off (D25: the toggle off means nothing changes
 * anywhere), while the reads are still in flight (a strip that flashes on every
 * screen load is one people learn to stop seeing), when a drawer IS open — the
 * ordinary case all night — or for a member who never settles.
 */
export function DrawerStrip() {
  const t = useTranslate();
  const { data: session } = useSession();
  const canManageShift = hasPermission(session, PERMISSIONS.SHIFT_MANAGE);
  // The rung the refusal is actually attached to — see the third audience above.
  const canSettle = hasPermission(session, PERMISSIONS.INVOICE_SETTLE);
  const [asked, setAsked] = useState(false);

  const settingsQuery = useApiQuery<RestaurantSettings>(
    queryKeys.settings,
    endpoints.restaurant.settings,
    { retry: false, enabled: canSettle },
  );
  const trackingOn = settingsQuery.data?.shiftTrackingEnabled === true;

  /*
   * Enabled only once settings SAY tracking is on — D25's rule applied to
   * requests, not just to pixels: a store that never opted in pays for no extra
   * call on the app's highest-traffic screens.
   */
  const currentQuery = useApiQuery<{
    id: string;
    label: string;
    openedAt: string;
  } | null>(queryKeys.shiftCurrent, endpoints.restaurant.shiftCurrent, {
    retry: false,
    // Not fetched at all for a member who never settles: the answer could not
    // change what they see, and this rides the app's highest-traffic screens.
    enabled: trackingOn && canSettle,
  });

  // `=== null`, never falsy: `undefined` is "still loading", and a strip that
  // appears while the answer is in flight would fire on every screen load.
  const noDrawer = trackingOn && canSettle && currentQuery.data === null;
  if (!noDrawer) return null;

  const sentence = canManageShift
    ? t((m) => m.pos.drawerStripHolder)
    : t((m) => m.pos.drawerStripAskManager);

  /*
   * A BADGE IN THE TOP BAR, not the full-width strip this shipped as
   * (founder direction, 2026-09-21 — D98's presentation, not its rule).
   *
   * Everything that decided WHEN to warn is untouched above: tracking on, the
   * settle rung, `=== null` rather than falsy, and nothing at all while the
   * reads are in flight. Only the shape changed.
   *
   * What the strip bought was that a cashier met it before touching anything,
   * and a quiet icon would hand that back — so this one is not quiet. It keeps
   * the warning fill and the warning ink, it sits in the bar on every till
   * screen, and it stays lit for as long as no drawer is open. The sentence the
   * strip printed is the accessible name and the tooltip, so nothing is lost to
   * a screen reader or to a hover; it is no longer printed across the page.
   *
   * `title` AND `aria-label`: the tooltip is for the sighted cashier who wants
   * to know what the icon means, the label for the one who never sees it.
   */
  return (
    <>
      <Button
        type="button"
        /* The app's own yellow button, not a hand-rolled square. `accent` is the
           variant that IS yellow: `#ffd23f` with a 2px ink border and a hard
           3px offset shadow under STACK, the brand gold in the default look. An
           earlier pass styled this by hand from `--accent`, which the till
           remaps to near-white on dark and near-black on light — so it came out
           any colour but yellow. Going through `Button` means the look's own
           rules paint it, the way they paint every other button in the app. */
        variant="accent"
        /* The glyph goes in `leadingIcon` with NO children, which is how
           `Button` decides a button is icon-only — and icon-only at `md` is
           34×34 with no padding, the exact box the language and theme buttons
           beside it occupy. Passed as a child instead, it is a labelled button
           with horizontal padding for a label that is not there, and the badge
           came out a wide pill next to two squares. */
        size="md"
        onClick={canManageShift ? () => setAsked(true) : undefined}
        // Not actionable for someone who cannot open a drawer — a control whose
        // only outcome is a refusal is the "dead instruction" the rule above
        // rejects. They still see it; there is just nothing to press.
        aria-disabled={canManageShift ? undefined : true}
        title={sentence}
        aria-label={sentence}
        data-testid="drawer-strip"
        className="no-print shrink-0"
        /*
         * INLINE, and it has to be. The look styles buttons by
         * `[data-ui="button"][data-size="md"]`, which out-specifies any utility
         * class `Button` sets — so its icon-only `p-0` lost and the button kept
         * 16px of padding each side. In a 36px border-box that leaves a content
         * box of ZERO, and the 16px glyph simply overflowed it: the icon read
         * as cropped, which is exactly what it was.
         *
         * A style attribute is the one thing that beats the look without an
         * `!important` arms race, and the numbers are the neighbours' own —
         * 34×34, the box `pos-theme-toggle` and the locale link occupy.
         */
        style={{ height: 34, width: 34, padding: 0 }}
        /* An open cash drawer, not a generic alert triangle: the icon has to
           say WHICH thing is wrong, since the sentence is now behind it. */
        leadingIcon={
          <svg
            width="16"
            height="16"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="2"
            strokeLinecap="round"
            strokeLinejoin="round"
            aria-hidden="true"
          >
            <path d="M3 10h18v9a1 1 0 0 1-1 1H4a1 1 0 0 1-1-1v-9Z" />
            <path d="M5 10V6a1 1 0 0 1 1-1h12a1 1 0 0 1 1 1v4" />
            <path d="M10 14h4" />
          </svg>
        }
      />
      <OpenDrawerDialog isOpen={asked} onClose={() => setAsked(false)} />
    </>
  );
}
